import { useState, useEffect } from 'react';
import {Sun, TrendingUp} from 'lucide-react';

export default function GreenhouseEffectAnimation() {
  const [ghgLevel, setGhgLevel] = useState(50);
  const [isAnimating, setIsAnimating] = useState(false);

  // Temperature increases with GHG level
  const baseTemp = 15;
  const temperature = baseTemp + (ghgLevel / 10);
  
  // Heat rays animation
  useEffect(() => {
    const interval = setInterval(() => {
      setIsAnimating(prev => !prev);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="bg-gradient-to-b from-blue-50 to-white rounded-2xl p-8 border border-gray-200">
      <h3 className="text-xl font-bold text-gray-900 mb-6">
        Interactive Greenhouse Effect Model
      </h3>

      {/* Visualization Area */}
      <div className="relative bg-gradient-to-b from-blue-400 to-blue-100 rounded-xl p-8 mb-6 overflow-hidden" style={{ height: '300px' }}>
        {/* Sun */}
        <div className="absolute top-4 right-4">
          <Sun className="w-16 h-16 text-yellow-400" />
        </div>

        {/* Sunlight Rays */}
        <div className="absolute top-20 right-12 space-y-2">
          {[1, 2, 3, 4].map((ray) => (
            <div
              key={ray}
              className={`h-1 bg-yellow-300 rounded transition-all duration-1000 ${
                isAnimating ? 'w-32' : 'w-24'
              }`}
              style={{
                opacity: 0.7,
                transform: `translateX(${isAnimating ? '20px' : '0px'})`,
              }}
            />
          ))}
        </div>

        {/* Greenhouse Gas Layer */}
        <div
          className="absolute bottom-0 left-0 right-0 bg-gray-400 transition-all duration-700 border-t-4 border-gray-500"
          style={{
            height: `${ghgLevel}%`,
            opacity: 0.3 + (ghgLevel / 200),
          }}
        >
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <p className="text-xs font-semibold text-gray-700">
                Greenhouse Gas Layer
              </p>
              <p className="text-xs text-gray-600">
                Traps heat from escaping
              </p>
            </div>
          </div>
        </div>

        {/* Heat Rays Escaping/Trapped */}
        <div className="absolute bottom-20 left-12 space-y-2">
          {[1, 2, 3].map((ray) => (
            <div
              key={ray}
              className={`h-1 rounded transition-all duration-1000`}
              style={{
                width: `${80 - ghgLevel * 0.5}px`,
                backgroundColor: ghgLevel > 50 ? '#ef4444' : '#fbbf24',
                opacity: 0.6,
                transform: `translateY(${isAnimating ? '-10px' : '0px'})`,
              }}
            />
          ))}
        </div>

        {/* Earth Surface */}
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-green-600 rounded-b-xl">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <TrendingUp className={`w-6 h-6 mx-auto mb-1 ${temperature > 17 ? 'text-red-400' : 'text-white'}`} />
              <p className="text-sm font-bold text-white">
                Earth Surface: {temperature.toFixed(1)}°C
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Control Slider */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-4">
          Greenhouse Gas Concentration: <span className="text-[#2E8B57] font-bold">{ghgLevel}%</span>
        </label>
        <input
          type="range"
          min="10"
          max="90"
          value={ghgLevel}
          onChange={(e) => setGhgLevel(parseInt(e.target.value))}
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#2E8B57]"
        />
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>Low (Pre-industrial)</span>
          <span>High (Current trend)</span>
        </div>
      </div>

      {/* Info Panel */}
      <div className={`p-4 rounded-lg border transition-all duration-300 ${
        temperature > 17 
          ? 'bg-red-50 border-red-200' 
          : temperature > 16 
          ? 'bg-orange-50 border-orange-200'
          : 'bg-green-50 border-green-200'
      }`}>
        <p className="text-sm text-gray-700">
          <strong>Current State:</strong> {
            temperature > 17 
              ? '🔥 High greenhouse gas levels trap excessive heat, leading to global warming.' 
              : temperature > 16
              ? '⚠️ Moderate greenhouse gas levels maintain habitable temperatures.'
              : '✅ Low greenhouse gas levels allow heat to escape naturally.'
          }
        </p>
        <p className="text-xs text-gray-600 mt-2">
          The greenhouse effect is natural and necessary, but human activities have increased GHG concentrations by 50% since pre-industrial times.
        </p>
      </div>
    </div>
  );
}
